import { AnimatePresence, motion, useReducedMotion } from 'framer-motion'
import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { useLiveAudience } from '../api'
import type { PublicEvent } from '../types'

// Un lecteur d'écran n'a pas à entendre le compteur toutes les 25 secondes :
// on annonce la bulle à son apparition, puis au plus une fois par période.
const ANNOUNCE_EVERY_MS = 5 * 60_000

/**
 * La bulle de l'accueil : combien de visiteurs sont là, combien sont passés.
 *
 * Posée dans le hero, au-dessus du logo — pas en surimpression fixe. Sur un
 * téléphone, un élément flottant finit toujours par masquer quelque chose
 * (un bouton, la barre « Prendre son billet ») ; ici, elle défile avec la
 * page et ne recouvre rien.
 *
 * Le total est celui de l'écran Audience (visiteurs distincts de chaque jour,
 * additionnés). « En ce moment » compte les onglets qui ont donné signe de vie
 * dans la fenêtre du serveur. Si l'un des deux manque — Redis absent, total
 * sous le seuil choisi par l'équipe — la bulle montre l'autre ; si les deux
 * manquent, ou que l'équipe l'a masquée, elle n'existe pas.
 */
export function VisitorBubble({ event }: { event: PublicEvent }) {
  const { t, i18n } = useTranslation()
  const reduce = useReducedMotion()
  // Le réglage `site.visitor_bubble` est lu par le serveur (`enabled`), pas
  // ici : ainsi, l'allumer ou l'éteindre se voit à la relecture suivante,
  // sans que le visiteur recharge la page.
  const { data } = useLiveAudience(event.slug)

  const number = new Intl.NumberFormat(i18n.language)
  // Le visiteur qui lit la bulle est lui-même sur le site : « 0 en ce moment »
  // serait faux. Cela arrive quand le compte a été lu juste avant son premier
  // signe de vie, ou s'il a demandé à ne pas être suivi.
  const now = data?.online_now != null ? Math.max(1, data.online_now) : null
  const total = data?.total_visitors ?? null
  const visible = Boolean(data?.enabled) && (now !== null || total !== null)

  const parts = [
    now !== null ? t('event.visitors.now', { count: now, value: number.format(now) }) : null,
    total !== null ? t('event.visitors.total', { count: total, value: number.format(total) }) : null,
  ].filter((part): part is string => part !== null)
  const sentence = parts.join(' · ')

  const [announced, setAnnounced] = useState('')
  const lastAnnounce = useRef(0)
  useEffect(() => {
    if (!visible || !sentence) return
    if (Date.now() - lastAnnounce.current < ANNOUNCE_EVERY_MS) return
    lastAnnounce.current = Date.now()
    setAnnounced(sentence)
  }, [visible, sentence])

  return (
    <>
      {/* Région annoncée, toujours montée : un `aria-live` créé en même temps
          que son contenu n'est pas lu par tous les lecteurs d'écran. */}
      <p className="sr-only" aria-live="polite" aria-atomic="true">
        {visible ? announced : ''}
      </p>

      <AnimatePresence>
        {visible ? (
          // `mt-4` : quand le hero dépasse l'écran, son contenu commence juste
          // sous l'en-tête fixe ; la bulle y était collée.
          <motion.p
            key="bubble"
            initial={reduce ? false : { opacity: 0, y: -6, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={reduce ? { opacity: 0, transition: { duration: 0 } } : { opacity: 0, y: -6 }}
            transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
            data-testid="visitor-bubble"
            className="glass mb-7 mt-4 inline-flex max-w-full items-center gap-2.5 rounded-full px-4 py-2 text-xs text-content-secondary shadow-md sm:text-sm"
          >
            {now !== null ? (
              <span aria-hidden className="relative flex h-2 w-2 shrink-0">
                {reduce ? null : (
                  <motion.span
                    className="absolute inset-0 rounded-full bg-ember-400"
                    animate={{ scale: [1, 2.4], opacity: [0.55, 0] }}
                    transition={{ duration: 2.2, repeat: Infinity, ease: 'easeOut' }}
                  />
                )}
                <span className="relative h-2 w-2 rounded-full bg-ember-400 shadow-ember" />
              </span>
            ) : null}
            <span className="sr-only">
              {t('event.visitors.label')} : {sentence}
            </span>
            <span aria-hidden className="min-w-0 tabular-nums">
              {parts.map((part, index) => (
                <span key={index}>
                  {index > 0 ? <span className="mx-1.5 text-content-muted">·</span> : null}
                  <span className={index === 0 ? 'text-content-primary' : undefined}>{part}</span>
                </span>
              ))}
            </span>
          </motion.p>
        ) : null}
      </AnimatePresence>
    </>
  )
}
